/**
 * RedirectUriHelp — erklärt einrichtungsverständlich, welche exakte Callback-URL
 * je Kanal im Entwickler-Portal (Google/Meta/TikTok/LinkedIn) eingetragen werden muss.
 *
 * Zeigt die kanonischen URIs aus dem Backend (/social-oauth/redirect-uris) mit
 * Ein-Klick-Kopieren. Vermeidet Dev-Sprache, nennt das passende Portal je Kanal.
 *
 * Einsatz: in der Setup-/Master-Ansicht der Kanal-Seite (Einrichtung ist eine
 * einmalige Inhaber-Aufgabe, nicht pro Endkunde).
 */
import { useEffect, useState } from "react";
import { Copy, CopyCheck, ChevronDown, ChevronUp, ShieldCheck, Loader2, CheckCircle, AlertTriangle } from "lucide-react";
import { fetchRedirectUris, fetchConfigCheck } from "../../services/socialOAuth";
import { PORTALS } from "./channelHelp";

/** Bereitschafts-Ampel für einen Kanal (grün=bereit, amber=Konfiguration fehlt). */
function ReadyBadge({ info }) {
  if (!info) return null;
  if (info.ready) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-600">
        <CheckCircle size={12} /> Bereit
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600">
      <AlertTriangle size={12} /> Konfiguration fehlt
    </span>
  );
}

// Portal-Texte aus dem geteilten channelHelp-Modul (siehe ./channelHelp)
const ORDER = ["google_business", "facebook", "tiktok", "linkedin", "threads", "pinterest"];

function CopyRow({ uri }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(uri);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* Clipboard nicht verfügbar — Nutzer kann manuell markieren */
    }
  };
  return (
    <div className="flex items-center gap-2 mt-1">
      <code className="flex-1 text-[11px] bg-stone-50 border border-stone-200 rounded px-2 py-1 text-stone-600 break-all">{uri}</code>
      <button onClick={copy} className="flex items-center gap-1 text-xs text-fox-fire hover:text-orange-700 flex-shrink-0" title="Kopieren">
        {copied ? <CopyCheck size={13} /> : <Copy size={13} />}
        {copied ? "Kopiert" : "Kopieren"}
      </button>
    </div>
  );
}

export default function RedirectUriHelp() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uris, setUris] = useState(null);
  const [base, setBase] = useState("");
  const [check, setCheck] = useState(null); // { channels, ready_count }

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [data, chk] = await Promise.all([fetchRedirectUris(), fetchConfigCheck()]);
        if (!active) return;
        setUris(data?.redirect_uris || {});
        setBase(data?.public_base_url || "");
        setCheck(chk || { channels: {} });
      } catch {
        if (active) { setUris({}); setCheck({ channels: {} }); }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const readyCount = check?.ready_count ?? null;
  const totalChannels = ORDER.length;

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 p-4 text-left">
        <div className="w-9 h-9 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <ShieldCheck size={18} className="text-sky-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-stone-800 text-sm">Einrichtung: Weiterleitungs-Adressen für die Portale</p>
          <p className="text-xs text-stone-500">Einmalig pro Kanal die exakte Adresse ins jeweilige Portal kopieren — sonst schlägt die Anmeldung fehl.</p>
        </div>
        {readyCount !== null && (
          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${readyCount === totalChannels ? "bg-green-100 text-green-600" : "bg-amber-100 text-amber-600"}`}>
            {readyCount}/{totalChannels} bereit
          </span>
        )}
        {open ? <ChevronUp size={16} className="text-stone-400" /> : <ChevronDown size={16} className="text-stone-400" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-stone-100 pt-3">
          {loading ? (
            <div className="flex items-center gap-2 text-stone-400 text-sm py-3"><Loader2 size={15} className="animate-spin" /> Lade Adressen…</div>
          ) : (
            <>
              {base && (
                <p className="text-xs text-stone-400">
                  Basis-Adresse dieser Umgebung: <code className="bg-stone-50 px-1 rounded">{base}</code>
                </p>
              )}
              {ORDER.filter((k) => uris?.[k]?.redirect_uri).map((k) => {
                const p = PORTALS[k];
                const info = check?.channels?.[k];
                return (
                  <div key={k} className="rounded-lg border border-stone-100 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="text-sm font-medium text-stone-700 truncate">{p.label}</p>
                        <ReadyBadge info={info} />
                      </div>
                      <span className="text-[11px] text-stone-400 flex-shrink-0">{p.portal}</span>
                    </div>
                    <p className="text-xs text-stone-500 mt-0.5">{p.where}</p>
                    <CopyRow uri={uris[k].redirect_uri} />
                    {info && !info.ready && info.missing_env?.length > 0 && (
                      <p className="text-[11px] text-amber-600 mt-1">
                        Noch zu hinterlegen: {info.missing_env.join(", ")}
                      </p>
                    )}
                  </div>
                );
              })}
              <p className="text-xs text-stone-400 leading-snug">
                Wichtig: Die Adresse muss <strong>exakt</strong> übereinstimmen (inkl. https und ohne Schrägstrich am Ende).
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
